import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Badge } from 'antd';
import {
  Bell,
  RefreshCw,
  LogOut,
  ChevronDown,
  User,
  Menu as MenuIcon,
  Sun,
  Moon,
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../utils/tailwindUtils';
import useApiMethods from '../../lib/useApiMethods';
import { LOGOUT, ACTIVE_ALERTS } from '../../constants/api';
import ToastMsg from '../common/ToastMsg';
import dayjs from 'dayjs';

/** Map pathname to page title */
const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/finance': 'Finance',
  '/hr': 'Human Resources',
  '/sales': 'Sales',
  '/support': 'Support',
  '/projects': 'Projects',
  '/alerts': 'Alerts',
  '/settings/users': 'User Management',
  '/settings/roles': 'Roles & Permissions',
};

const Header = ({ onMenuClick, alertCount: externalAlertCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const { isDark, toggleTheme } = useTheme();
  const { fetch, create } = useApiMethods();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(externalAlertCount || 0);
  const [lastRefreshed, setLastRefreshed] = useState(dayjs());
  const [refreshing, setRefreshing] = useState(false);
  const userMenuRef = useRef(null);

  const userName = sessionStorage.getItem('login_fname') || sessionStorage.getItem('login_name') || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  // Resolve page title from current path
  const pageTitle =
    ROUTE_TITLES[location.pathname] ||
    location.pathname
      .split('/')
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' > ') ||
    'Dashboard';

  // Fetch alert count
  const fetchAlertCount = useCallback(async () => {
    try {
      const res = await fetch(ACTIVE_ALERTS, { limit: 1 });
      if (res?.count !== undefined) {
        setAlertCount(res.count);
      }
    } catch (_) {}
  }, [fetch]);

  useEffect(() => {
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 120000); // every 2 min
    return () => clearInterval(interval);
  }, [fetchAlertCount]);

  // Close user menu on outside click
  useEffect(() => {
    const handle = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    setLastRefreshed(dayjs());
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      await create(LOGOUT, {});
    } catch (_) {
      // proceed with client-side logout even if server call fails
    }
    logout();
    window.location.href = '/';
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-20 w-full border-b bg-background/95 backdrop-blur',
        'supports-[backdrop-filter]:bg-background/60 transition-colors duration-200'
      )}
    >
      <div className="flex h-14 items-center justify-between px-4 sm:px-6">
        {/* Left: mobile menu + title */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg hover:bg-accent text-muted-foreground"
          >
            <MenuIcon size={20} />
          </button>
          <h1 className="text-base font-semibold text-foreground truncate">
            {pageTitle}
          </h1>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          {/* Last refreshed (hidden on mobile) */}
          <span className="hidden lg:block text-xs text-muted-foreground mr-2">
            Last refreshed: {lastRefreshed.format('h:mm A')}
          </span>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-accent text-muted-foreground transition-colors"
            title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            className={cn(
              'p-2 rounded-lg hover:bg-accent text-muted-foreground transition-colors',
              refreshing && 'animate-spin'
            )}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>

          {/* Alert bell */}
          <button
            onClick={() => navigate('/alerts')}
            className="relative p-2 rounded-lg hover:bg-accent text-muted-foreground transition-colors"
            title="Alerts"
          >
            <Bell size={18} />
            {alertCount > 0 && (
              <span className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-[9px] font-bold items-center justify-center">
                  {alertCount > 9 ? '9+' : alertCount}
                </span>
              </span>
            )}
          </button>

          {/* User dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors',
                'hover:bg-accent border border-transparent hover:border-border',
                userMenuOpen && 'bg-accent border-border'
              )}
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                {userInitial}
              </div>
              <span className="hidden sm:block text-sm font-medium text-foreground max-w-[120px] truncate">
                {userName}
              </span>
              <ChevronDown
                size={16}
                className={cn(
                  'text-muted-foreground transition-transform',
                  userMenuOpen && 'rotate-180'
                )}
              />
            </button>

            {userMenuOpen && (
              <div
                className={cn(
                  'absolute right-0 top-12 w-64 rounded-xl border shadow-lg z-50 overflow-hidden',
                  'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                )}
              >
                {/* User info header */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30">
                  <p className="text-sm font-semibold text-foreground truncate capitalize">
                    {sessionStorage.getItem('login_fname')}{' '}
                    {sessionStorage.getItem('login_lname')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {sessionStorage.getItem('role_name') || 'User'}{' '}
                    {sessionStorage.getItem('department')
                      ? `| ${sessionStorage.getItem('department')}`
                      : ''}
                  </p>
                </div>

                {/* Logout */}
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleLogout}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors',
                      'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/40'
                    )}
                  >
                    <LogOut size={16} />
                    <span className="font-medium">Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
